import styled from 'styled-components';
import {
    error,
    robotoRegular,
    scarpaGray25,
    scarpaGray50,
    scarpaGray100,
    scarpaGray200,
    scarpaGray300,
    scarpaGray400,
    scarpaGray500,
    scarpaGray800,
    scarpaGray900,
    spanishViridian25,
    spanishViridian500,
    spanishViridian600,
    white,
} from '@habitech/shared';

/** Tarjeta principal de la tab "Información del usuario". */
export const OuterCard = styled.div`
    font-family: ${robotoRegular};
    margin-top: 20px;
    background: ${white};
    border: 1px solid ${scarpaGray200};
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
    color: ${scarpaGray900};
    overflow: hidden;
`;

export const OuterHeader = styled.button`
    width: 100%;
    border: none;
    background: ${white};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 22px;
    font-family: ${robotoRegular};
    color: ${scarpaGray900};

    .header-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 16px;
        font-weight: 700;
    }

    .header-chevron {
        color: ${scarpaGray500};
        flex-shrink: 0;
    }
`;

export const OuterBody = styled.div`
    font-family: ${robotoRegular};
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 22px 18px;
`;

/* ---------- Contacto básico ---------- */

export const ContactCard = styled.section`
    font-family: ${robotoRegular};
    border: 1px solid ${scarpaGray200};
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const ContactCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

export const SectionPill = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: ${robotoRegular};
    font-size: 15px;
    font-weight: 700;
    color: ${scarpaGray900};

    .pill-chevron {
        color: ${scarpaGray500};
    }
`;

export const EditButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 16px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-family: ${robotoRegular};
    font-size: 13px;
    font-weight: 600;
    color: ${white};
    background: ${scarpaGray900};

    &:hover {
        opacity: 0.92;
    }
`;

export const ContactColumns = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const InfoRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    border-radius: 10px;
    background: ${scarpaGray25};
`;

export const InfoRowLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${scarpaGray500};

    .row-icon {
        color: ${scarpaGray500};
    }
`;

export const InfoRowValue = styled.span<{ $bold?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    text-align: right;
    font-size: 14px;
    font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
    color: ${scarpaGray900};
    word-break: break-word;
`;

/** Distintivo verde para canales verificados por OTP. */
export const OtpBadge = styled.span<{ $verified?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 999px;
    background: ${({ $verified }) => ($verified === false ? '#FCE9E9' : spanishViridian25)};
    border: 1px solid ${({ $verified }) => ($verified === false ? error : spanishViridian500)};
    color: ${({ $verified }) => ($verified === false ? error : spanishViridian600)};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.3px;

    svg {
        flex-shrink: 0;
    }
`;

/**
 * Distintivo informativo (solo lectura) del estado de un producto/canal:
 * verde "Activo" cuando está habilitado, gris atenuado "Inactivo" cuando no.
 * Reemplaza a los antiguos toggles (que aparentaban ser interactivos).
 */
export const StatusPill = styled.span<{ $on?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 999px;
    background: ${({ $on }) => ($on ? spanishViridian25 : scarpaGray50)};
    border: 1px solid ${({ $on }) => ($on ? spanishViridian500 : scarpaGray200)};
    color: ${({ $on }) => ($on ? spanishViridian600 : scarpaGray500)};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.3px;
    white-space: nowrap;
    flex-shrink: 0;

    svg {
        flex-shrink: 0;
    }
`;

/* ---------- Acordeones (PCOM / Productos a expirar) ---------- */

export const AccordionBar = styled.button<{ $open?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px;
    border: 1px solid ${scarpaGray200};
    border-radius: ${({ $open }) => ($open ? '12px 12px 0 0' : '12px')};
    border-bottom: ${({ $open }) => ($open ? 'none' : `1px solid ${scarpaGray200}`)};
    background: ${white};
    cursor: pointer;
    font-family: ${robotoRegular};
    font-size: 15px;
    font-weight: 700;
    color: ${scarpaGray900};

    .bar-chevron {
        color: ${scarpaGray500};
    }
`;

export const AccordionContent = styled.div`
    font-family: ${robotoRegular};
    border: 1px solid ${scarpaGray200};
    border-top: none;
    border-radius: 0 0 12px 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const Footer = styled.div`
    font-family: ${robotoRegular};
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: ${scarpaGray500};
    padding-top: 4px;

    .footer-icon {
        color: ${spanishViridian500};
    }
`;

/* ---------- Información PCOM ---------- */

export const PcomProductsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 10px;
`;

export const ProductChip = styled.div<{ $on?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid ${({ $on }) => ($on ? spanishViridian500 : scarpaGray200)};
    border-radius: 10px;
    background: ${({ $on }) => ($on ? spanishViridian25 : white)};
    min-width: 0;
`;

export const ChipMain = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

export const ChipIcon = styled.span<{ $on?: boolean }>`
    display: inline-flex;
    flex-shrink: 0;
    color: ${({ $on }) => ($on ? spanishViridian500 : scarpaGray400)};
`;

export const ChipLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${scarpaGray800};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 16px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const MetricRow = styled.div<{ $highlight?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    border-radius: 10px;
    background: ${({ $highlight }) => ($highlight ? '#FBF6E7' : scarpaGray25)};
    border: 1px solid ${({ $highlight }) => ($highlight ? '#F0DCA3' : 'transparent')};
`;

export const MetricLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: ${scarpaGray500};
`;

export const MetricValue = styled.span<{ $highlight?: boolean }>`
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    flex-shrink: 0;
    font-size: 15px;
    font-weight: 700;
    text-align: right;
    color: ${({ $highlight }) => ($highlight ? '#C9851F' : scarpaGray900)};
`;

export const MetricHint = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${scarpaGray500};
`;

/* ---------- Tabla "Productos a expirar" (sin cambios) ---------- */

export const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`;

export const ExpirationTable = styled.table`
    font-family: ${robotoRegular};
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;
    border: 1px solid ${scarpaGray200};
    border-radius: 12px;
    overflow: hidden;

    th,
    td {
        padding: 12px 14px;
        text-align: left;
        border-bottom: 1px solid ${scarpaGray100};
        white-space: nowrap;
    }

    th.center,
    td.center {
        text-align: center;
    }

    thead th {
        background: ${scarpaGray50};
        color: ${scarpaGray500};
        font-weight: 700;
        font-size: 11px;
        letter-spacing: 0.4px;
        text-transform: uppercase;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    td.month {
        font-weight: 600;
        color: ${scarpaGray900};
        text-transform: capitalize;
    }
`;

export const ProductCell = styled.span`
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    color: ${scarpaGray900};

    &[data-empty='true'] {
        color: ${scarpaGray300};
        font-weight: 500;
    }

    &[data-active='true'] {
        color: ${spanishViridian600};
        font-weight: 700;
    }
`;

export const StatusBadge = styled.span<{ $danger?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ $danger }) => ($danger ? error : spanishViridian600)};
`;

export const EmptyExpiration = styled.div`
    font-size: 13px;
    color: ${scarpaGray500};
    padding: 8px 0;
`;
